import i18n from '../translate';
import { logger } from './logger';

const playNotificationSound = () => {
  try {
    const soundEnabled = localStorage.getItem('notificationSoundEnabled');
    if (soundEnabled === 'false') {
      return;
    }

    const AudioContextClass =
      window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) {
      return;
    }

    const audioContext = new AudioContextClass();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.1);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  } catch (error) {
    logger.error('Failed to play payment expired notification sound:', error);
  }
};

const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window) || typeof Notification.requestPermission !== 'function') {
    return 'denied';
  }

  try {
    const permissionResult = Notification.requestPermission();
    if (permissionResult && typeof permissionResult.then === 'function') {
      return await permissionResult;
    }

    return await new Promise<NotificationPermission>((resolve) => {
      Notification.requestPermission((permission) => resolve(permission));
    });
  } catch (error) {
    logger.error('Failed to request notification permission:', error);
    return 'denied';
  }
};

export const showPaymentExpiredNotification = async (): Promise<Notification | null> => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return null;
  }

  if (localStorage.getItem('notificationsEnabled') === 'false') {
    return null;
  }

  if (location.protocol !== 'https:' && !location.hostname.includes('localhost')) {
    logger.warn('Skipping payment notification because the current origin is not secure');
    return null;
  }

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await requestNotificationPermission();
  }

  if (permission !== 'granted') {
    return null;
  }

  try {
    const notification = new Notification(i18n.t('qr_code_expired', 'QR Code Expired'), {
      body: i18n.t('payment_time_expired', 'Payment time has expired. Please try again.'),
      icon: `${window.location.origin}/icon-192x192.svg`,
      badge: `${window.location.origin}/icon-192x192.svg`,
      tag: 'payment-expired',
      requireInteraction: true,
      silent: false,
    });

    notification.onclick = () => {
      try {
        window.focus();
        notification.close();
      } catch (error) {
        logger.warn('Failed to focus window for payment expired notification:', error);
      }
    };

    setTimeout(() => {
      try {
        notification.close();
      } catch (error) {
        logger.warn('Failed to auto-close payment expired notification:', error);
      }
    }, 8000);

    playNotificationSound();
    return notification;
  } catch (error) {
    logger.error('Failed to show payment expired notification:', error);
    return null;
  }
};
